import type { ThoughtStep } from '@/utils/aiThought';

export type TemplateGeneratePhaseKey =
  | 'intent_bind'
  | 'outline_plan'
  | 'section_write'
  | 'structure_check'
  | 'quality_review';

export type TemplateGenerateStepStatus = 'pending' | 'processing' | 'done' | 'failed';

export type TemplateGenerateProgressStep = {
  key?: TemplateGeneratePhaseKey | string;
  title?: string;
  status?: TemplateGenerateStepStatus | string;
  text?: string;
};

const STEP_META: Record<TemplateGeneratePhaseKey, { title: string; pendingText: string; processingText: string }> = {
  intent_bind: {
    title: '理解需求',
    pendingText: '等待确认需求…',
    processingText: '正在确认要生成的文档与项目信息…',
  },
  outline_plan: {
    title: '规划结构',
    pendingText: '等待规划章节…',
    processingText: '正在梳理文档章节与填写范围…',
  },
  section_write: {
    title: '撰写内容',
    pendingText: '等待撰写…',
    processingText: '正在按章节写入项目资料…',
  },
  structure_check: {
    title: '核对结构',
    pendingText: '等待结构核对…',
    processingText: '正在核对必填章节与表格结构…',
  },
  quality_review: {
    title: '内容核对',
    pendingText: '等待内容核对…',
    processingText: '正在核对关键信息与待确认项…',
  },
};

const STEP_ORDER: TemplateGeneratePhaseKey[] = [
  'intent_bind',
  'outline_plan',
  'section_write',
  'structure_check',
  'quality_review',
];

export const TEMPLATE_GENERATE_LIVE_STEPS: ThoughtStep[] = STEP_ORDER.map((key) => ({
  key,
  icon: '⏳',
  title: STEP_META[key].title,
  text: STEP_META[key].processingText,
}));

export function templateGenerateThoughtInitial(): ThoughtStep[] {
  return [
    {
      key: 'intent_bind',
      icon: '⏳',
      title: STEP_META.intent_bind.title,
      text: STEP_META.intent_bind.processingText,
    },
  ];
}

function normalizeStatus(raw?: string): TemplateGenerateStepStatus {
  const s = String(raw || '').toLowerCase();
  if (s === 'done' || s === 'completed' || s === 'success') return 'done';
  if (s === 'failed' || s === 'error') return 'failed';
  if (s === 'processing' || s === 'running' || s === 'in_progress') return 'processing';
  return 'pending';
}

/** 将后端 processSteps / thought.steps 转为思考步骤 */
export function mapTemplateGenerateProgressToThought(
  steps?: TemplateGenerateProgressStep[] | null,
  thoughtSteps?: Array<{ key?: string; title?: string; text?: string; icon?: string }> | null,
): ThoughtStep[] {
  if (thoughtSteps?.length) {
    return thoughtSteps
      .filter((s) => s?.key)
      .map((s) => ({
        key: String(s.key),
        icon: s.icon || '⏳',
        title: (s.title || STEP_META[s.key as TemplateGeneratePhaseKey]?.title || s.key || '').trim(),
        text: (s.text || '').trim() || STEP_META[s.key as TemplateGeneratePhaseKey]?.processingText || '',
      }));
  }
  if (!steps?.length) {
    return templateGenerateThoughtInitial();
  }
  const byKey = new Map<string, TemplateGenerateProgressStep>();
  for (const s of steps) {
    if (s?.key) byKey.set(String(s.key), s);
  }
  const out: ThoughtStep[] = [];
  for (const key of STEP_ORDER) {
    const raw = byKey.get(key);
    const status = normalizeStatus(raw?.status);
    if (status === 'pending') break;
    const meta = STEP_META[key];
    const text = (raw?.text || '').trim()
      || (status === 'done' ? `${meta.title}已完成` : meta.processingText);
    out.push({
      key,
      icon: status === 'done' ? '✅' : status === 'failed' ? '❌' : '⏳',
      title: (raw?.title || meta.title).trim() || meta.title,
      text,
    });
    if (status === 'failed') break;
  }
  return out.length ? out : templateGenerateThoughtInitial();
}

/** 开写前的理解确认文案（前端首条气泡） */
export function buildTemplateGenerateUnderstandingMessage(opts: {
  continuing?: boolean;
  templateTitle?: string | null;
  projectName?: string | null;
}): string {
  const template = (opts.templateTitle || '').trim() || '企业模版';
  const project = (opts.projectName || '').trim();
  const head = opts.continuing ? '已收到补充信息，继续生成' : '好的，开始生成';
  if (project) {
    return `${head}《${template}》——项目「${project}」。约需片刻，生成过程中右侧可实时预览。`;
  }
  return `${head}《${template}》。约需片刻，生成过程中右侧可实时预览。`;
}
